import { PropsWithChildren, useMemo } from "react";
import { Navigate, Outlet, RouteObject, useParams } from "react-router-dom";
import { Box, Container, Typography as T } from "@mui/material";
import BrowseConceptsLayout from "catalog/components/BrowseConceptsLayout";
import CatalogSearch from "catalog/components/CatalogSearch";
import ConceptDetail from "catalog/components/ConceptDetail";
import ConceptGroupList from "catalog/components/ConceptGroupList";
import ConceptSearch from "catalog/components/ConceptSearch";
import CatalogDataset from "catalog/components/Dataset";
import CatalogDatasets from "catalog/components/Datasets";
import CatalogPins from "catalog/components/Pins";
import CatalogVariables from "catalog/components/Variables";
import useCatalogEnabled from "catalog/hooks/useCatalogEnabled";
import useTagDisplayMappings from "catalog/hooks/useTagDisplayMappings";
import DatasetDetails from "datasets/pages/DatasetDetails";
import Datasets from "datasets/pages/Datasets";
import FlatCard from "~/components/FlatCard";
import Loading from "~/components/Loading";
import { ProtectedAppRoute } from "~/components/ProtectedAppRoute";
import RouteTabs, { RouteTab } from "~/components/RouteTabs";
import ScrollableTabContent from "~/components/ScrollableTabContent";
import useAppHeader from "~/contexts/header";
import useUser from "~/contexts/user";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import useGlobalFileBrowserEnabled from "~/hooks/useGlobalFileBrowserEnabled";
import { useSafeRoutes } from "~/hooks/useSafeRoutes";
import Report from "~/pages/Report";
import Reports from "~/pages/Reports";
import GlobalFilesView from "~/pages/files/GlobalFilesView";
import { ROUTE_404 } from "~/router/constants";

const { VITE_SIGMA_REPORT_IDS, VITE_DATA_DASHBOARD_URL } = import.meta.env;

/**
 * Guard component that redirects to 404 if catalog feature is not enabled.
 * Waits for user to load before making redirect decisions to avoid premature redirects.
 */
const CatalogGuard = ({ children }: PropsWithChildren) => {
  const user = useUser();
  const catalogEnabled = useCatalogEnabled();

  // Wait for user to load before deciding to redirect
  if (user === null) {
    return <Loading />;
  }

  if (!catalogEnabled) {
    return <Navigate to={ROUTE_404} replace />;
  }

  return <>{children}</>;
};

interface CatalogPageLayoutProps {
  title: string;
  children: React.ReactNode;
}

/**
 * Wrapper for catalog pages to provide consistent layout and search
 * Title and search controls live outside the card in the dimmed area
 * Uses ScrollableTabContent to contain scrollbar within content area (excludes header and tabs)
 */
const CatalogPageLayout = ({ title, children }: CatalogPageLayoutProps) => {
  // Initialize tag display mappings for catalog components
  useTagDisplayMappings();

  return (
    <ScrollableTabContent bgcolor="common.dimmedPageBackground">
      <Box pt={2} pb={3}>
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <T variant="h6" fontWeight={600}>
              {title}
            </T>
            <CatalogSearch />
          </Box>
          <FlatCard sx={{ px: 2 }}>{children}</FlatCard>
        </Container>
      </Box>
    </ScrollableTabContent>
  );
};

const routeDefinitions: RouteObject[] = [
  // Default route redirects to Datasets if needed
  { index: true, element: <Datasets /> },

  // Datasets list
  {
    path: "datasets",
    element: <Datasets />,
  },

  // Dataset details (has its own tab navigation)
  {
    path: "datasets/:datasetId/*",
    element: <DatasetDetails />,
  },

  // Global files
  {
    path: "files/*",
    element: <GlobalFilesView />,
  },

  // Dashboards list
  {
    path: "dashboards",
    element: <Reports />,
  },

  // Dashboard report detail (manages its own header)
  {
    path: "dashboards/:reportType",
    element: <Report />,
  },

  // Semantic Catalog: Concepts (nested under dashboards for legacy support)
  {
    path: "dashboards/concepts/search",
    element: <ConceptSearch />,
  },
  {
    path: "dashboards/concepts/:groupId",
    element: (
      <BrowseConceptsLayout>
        <ConceptGroupList />
      </BrowseConceptsLayout>
    ),
  },
  {
    path: "dashboards/concepts/:conceptId/details",
    element: (
      <BrowseConceptsLayout>
        <ConceptDetail />
      </BrowseConceptsLayout>
    ),
  },

  // Catalog: Tables
  {
    path: "tables",
    element: (
      <CatalogGuard>
        <ProtectedAppRoute permission="view:catalog-app">
          <CatalogPageLayout title="Tables">
            <CatalogDatasets />
          </CatalogPageLayout>
        </ProtectedAppRoute>
      </CatalogGuard>
    ),
  },

  // Catalog: Table detail (manages its own layout and header)
  {
    path: "tables/:tableFQN",
    element: (
      <CatalogGuard>
        <ProtectedAppRoute permission="view:catalog-app">
          <Box minHeight="100%" py={2} bgcolor="common.dimmedPageBackground">
            <Container maxWidth="lg">
              <CatalogDataset />
            </Container>
          </Box>
        </ProtectedAppRoute>
      </CatalogGuard>
    ),
  },

  // Catalog: Columns
  {
    path: "columns",
    element: (
      <CatalogGuard>
        <ProtectedAppRoute permission="view:catalog-app">
          <CatalogPageLayout title="Columns">
            <CatalogVariables />
          </CatalogPageLayout>
        </ProtectedAppRoute>
      </CatalogGuard>
    ),
  },

  // Catalog: Pins (has its own custom layout with separate cards)
  {
    path: "pins",
    element: (
      <CatalogGuard>
        <ProtectedAppRoute permission="view:catalog-app">
          <CatalogPins />
        </ProtectedAppRoute>
      </CatalogGuard>
    ),
  },
];

const DataBrowser = () => {
  const user = useUser();
  const { "*": subRoute } = useParams<{ "*": string }>();
  const routes = useSafeRoutes(routeDefinitions);

  // Feature flags and permissions
  const globalFileBrowserEnabled = useGlobalFileBrowserEnabled();
  const catalogEnabled = useCatalogEnabled();
  const dashboardsEnabled = !!(VITE_SIGMA_REPORT_IDS || VITE_DATA_DASHBOARD_URL);

  // Determine current tab from route
  const currentTab = subRoute?.split("/")[0] || "datasets";

  // Set document title based on current tab (only for list-level pages, not details)
  const tabTitles: Record<string, string> = {
    datasets: "Datasets",
    files: "Files",
    dashboards: "Dashboards",
    tables: "Tables",
    columns: "Columns",
    pins: "Pins",
  };
  useDocumentTitle(tabTitles[currentTab]);

  // Check if we're viewing a detail page that should hide RouteTabs
  // Detail pages manage their own headers via breadcrumbs
  const isDetailPage = useMemo(() => {
    if (!subRoute) {
      return false;
    }
    const parts = subRoute.split("/");

    // DatasetDetails (datasets/:datasetId/*)
    if (parts[0] === "datasets" && parts.length > 1) {
      return true;
    }

    // Table detail (tables/:tableFQN)
    if (parts[0] === "tables" && parts.length > 1) {
      return true;
    }

    // Dashboard detail pages: concepts (dashboards/concepts/*) and reports (dashboards/:reportType)
    if (parts[0] === "dashboards" && parts.length > 1) {
      return true;
    }

    return false;
  }, [subRoute]);

  // Memoize tabs to prevent unnecessary re-renders
  // Must be called before any early returns to follow rules of hooks

  const tabs: RouteTab[] = useMemo(
    () => [
      { label: "Datasets", value: "datasets", visible: true },
      { label: "Files", value: "files", visible: globalFileBrowserEnabled },
      { label: "Dashboards", value: "dashboards", visible: dashboardsEnabled },
      { label: "Tables", value: "tables", visible: catalogEnabled },
      { label: "Columns", value: "columns", visible: catalogEnabled },
      { label: "Pins", value: "pins", visible: catalogEnabled },
    ],
    [globalFileBrowserEnabled, dashboardsEnabled, catalogEnabled]
  );

  // Get only visible tabs for single-tab detection
  const visibleTabs = useMemo(() => tabs.filter((tab) => tab.visible !== false), [tabs]);
  const isSingleTab = visibleTabs.length === 1;
  const singleTabPath = isSingleTab ? visibleTabs[0].value : null;

  // Check if current tab is valid (exists in visible tabs)
  const isValidTab = visibleTabs.some((tab) => tab.value === currentTab);
  const firstVisibleTab = visibleTabs[0]?.value;

  // Detail pages handle their own header/breadcrumbs
  // For list-level tabs (including single-tab mode), we set the header to "Data Browser"
  useAppHeader({ title: isDetailPage ? undefined : "Data Browser" });

  // Detail pages don't show RouteTabs - they use header breadcrumbs for navigation
  if (isDetailPage) {
    return (
      <>
        {routes}
        <Outlet />
      </>
    );
  }

  // Known tab values that require async permission checks (catalog routes)
  const catalogTabs = ["tables", "columns", "pins"];
  const isCatalogTab = catalogTabs.includes(currentTab);

  // Redirect invalid tabs to first available tab (keeps URL in sync with content)
  // Wait for user to load before redirecting to avoid race conditions with permission checks
  // For catalog tabs, let CatalogGuard handle the redirect to avoid race with async permission checks
  if (!isValidTab && firstVisibleTab && subRoute && user !== null && !isCatalogTab) {
    return <Navigate to={firstVisibleTab} replace />;
  }

  // Single tab mode: redirect index to the only available tab and skip tab UI
  if (isSingleTab) {
    // If at index route, redirect to the only available tab
    if (!subRoute || subRoute === "") {
      return <Navigate to={singleTabPath!} replace />;
    }

    // Render content directly without tab navigation
    return (
      <>
        {routes}
        <Outlet />
      </>
    );
  }

  // Show tabs for list-level pages when multiple tabs are available
  return (
    <RouteTabs tabs={tabs} currentTab={currentTab}>
      {routes}
      <Outlet />
    </RouteTabs>
  );
};

export default DataBrowser;
